import ApiClientAsync from "../../api/ApiClientAsync";
import config from "../../config";

export const DYNAMIC_AUTOCOMPLETE_SUGGESTION_LIMIT = 20;
export const DYNAMIC_AUTOCOMPLETE_DEBOUNCE_MS = 200;

/**
 * Build absolute request URL from BE filter_data.url + phrase.
 * apiUrl looks like `https://host/:locale/api/`; filter_data.url is typically
 * `/api/stock/fast-search/` or `stock/fast-search/`.
 */
export function buildDynamicAutocompleteRequestUrl({
  filterDataUrl,
  phrase,
  apiBaseUrl,
  limit = DYNAMIC_AUTOCOMPLETE_SUGGESTION_LIMIT
}) {
  const base = String(apiBaseUrl || "")
    .trim()
    .replace(/\/+$/, "");
  let path = String(filterDataUrl || "")
    .trim()
    .split("?")[0]
    .replace(/\/+$/, "");

  if (!base || !path) {
    return null;
  }

  if (/\/api$/i.test(base) && /^\/?api(\/|$)/i.test(path)) {
    path = path.replace(/^\/?api\/?/i, "");
  } else {
    path = path.replace(/^\//, "");
  }

  if (!path) {
    return null;
  }

  return `${base}/${path}/${encodeURIComponent(phrase)}?limit=${limit}`;
}

export function normalizeDynamicAutocompleteResults(data) {
  const results = Array.isArray(data?.results) ? data.results : [];

  return results
    .map(item => {
      const stock = item?.stock || item;
      if (!stock || typeof stock.ticker !== "string" || !stock.ticker) {
        return null;
      }

      return {
        ticker: stock.ticker,
        name: stock.name || "",
        market: stock.market,
        id: stock.id
      };
    })
    .filter(Boolean);
}

/**
 * Fetch suggestions for a dynamic_autocomplete column using filter_data.url.
 * Empty phrase returns [] without a network call.
 */
export async function fetchDynamicAutocompleteSuggestions({
  filterDataUrl,
  phrase,
  locale,
  authToken,
  limit = DYNAMIC_AUTOCOMPLETE_SUGGESTION_LIMIT
}) {
  const trimmed = typeof phrase === "string" ? phrase.trim() : "";

  if (!trimmed) {
    return { suggestions: [] };
  }

  if (typeof filterDataUrl !== "string" || !filterDataUrl.trim()) {
    console.error(
      "Missing dynamic_autocomplete filter_data.url:",
      filterDataUrl
    );
    return { suggestions: [] };
  }

  try {
    const apiBaseUrl = config.apiUrl.replace(":locale", locale);
    const url = buildDynamicAutocompleteRequestUrl({
      filterDataUrl,
      phrase: trimmed,
      apiBaseUrl,
      limit
    });

    if (!url) {
      console.error(
        "Could not build dynamic_autocomplete request URL from:",
        filterDataUrl
      );
      return { suggestions: [] };
    }

    const apiClient = new ApiClientAsync(locale, authToken);
    const data = await apiClient.get({ url });

    return { suggestions: normalizeDynamicAutocompleteResults(data) };
  } catch (error) {
    console.error(error);
    return { suggestions: [] };
  }
}
